<template>
  <nav>
    <VTabsV2
      :tabs="tabs"
      base-url="/flow/orders"
      @update="onTabUpdate"
    />

    <OrdersPageFilterComponent
      :filters="filter"
      :show-delivery-options="activeTab?.id === 'in-work'"
      :show-payment-options="activeTab?.id === 'in-work'"
      @orders-page-filter:updated="onFilterUpdated"
    />
  </nav>

  <!-- Черновики заказов -->
  <OrdersRegistryExpanded
    v-if="activeTab?.id === 'draft'"
    ref="draftRegistry"
    :params="registryParams"
    is-draft
  />

  <!-- Позиции заказов (в работе / завершённые / удалённые / полученные) -->
  <OrderPositionsRegistry
    v-if="positionState !== null"
    :state="positionState"
    :hidden-columns="['status', 'responsible']"
    :params="registryParams"
    :show-action-bar="positionState === 'in-work'"
    @positions:updated="loadCounts"
  />

  <!-- Сборки, готовые к отгрузке -->
  <AssembliesRegistryExpanded
    v-if="activeTab?.id === 'ready-for-shipment'"
    :params="assembliesParams"
  />

  <!-- Отгрузки в доставке -->
  <ShipmentsRegistryExpanded
    v-if="activeTab?.id === 'shipped'"
    :params="baseParams"
    :is-received="false"
  />

  <!-- Заявки на оплату -->
  <PaymentRequestsRegistry
    v-if="activeTab?.id === 'payments'"
    :params="baseParams"
  />
</template>

<script lang="ts">
import Core                        from '@/core/Core';
import AssembliesRegistryExpanded  from '@/domains/Assemblies/components/AssembliesRegistryExpanded/AssembliesRegistryExpanded';
import ShipmentsRegistryExpanded   from '@/domains/Shipments/components/ShipmentsRegistryExpanded/ShipmentsRegistryExpanded.vue';
import OrderPositionsRegistry      from '@/domains/Orders/components/OrderPositionsRegistry/OrderPositionsRegistry.vue';
import OrdersPageFilterComponent   from './OrdersPageFilter/OrdersPageFilterComponent.vue';
import PaymentRequestsRegistry     from '@/domains/PaymentRequests/components/PaymentRequestsRegistry/PaymentRequestsRegistry.vue';
import OrdersService               from '@/domains/Orders/OrdersService';
import OrdersPageFilter            from './OrdersPageFilter/OrdersPageFilter';
import OrdersRegistryExpanded      from "@/domains/Orders/components/OrdersRegistryExpanded/OrdersRegistryExpanded.vue";
import TabElement                  from '@/lib/components/VTabsV2/TabElement';

/**
 * Вкладки потока заказов. Источник правды для VTabsV2 на этой странице.
 * Лежат в компоненте, а не в сервисе: набор вкладок описывает именно эту
 * страницу и нигде больше не переиспользуется.
 *
 * Поля:
 * - countKey:          ключ в ответе getCounts (camelCase, как отдаёт бэк); null = без счётчика
 * - hidden:            true = прячется в выпадашку «ещё»
 * - allowedPartnerIds: null = всем; иначе только перечисленным партнёрам (права доступа)
 * - roles:             роли, для которых вкладка существует
 */
const FLOW_TABS = [
  { id: 'draft',              name: 'Черновики',         countKey: 'draft',            hidden: false, allowedPartnerIds: [67, 497], roles: ['buyer'] },
  { id: 'in-work',            name: 'В работе',          countKey: 'inWork',           hidden: false, allowedPartnerIds: null,      roles: ['buyer', 'seller'] },
  { id: 'ready-for-shipment', name: 'Готовы к отгрузке', countKey: 'readyForShipment', hidden: false, allowedPartnerIds: null,      roles: ['buyer', 'seller'] },
  { id: 'shipped',            name: 'В пути',            countKey: 'shipped',          hidden: false, allowedPartnerIds: null,      roles: ['buyer', 'seller'] },
  // Причина по которой ЗТК здесь не разместить, это фильтр страницы ЗТК. У станицы ЗТК фильтр не только по В работе, утвреждён и завершён, а и по ответственному.
  // в Воронке заказа нет под это места
  // { id: 'transport-orders',   name: 'ЗТК',               countKey: null,               hidden: false, allowedPartnerIds: [497],     roles: ['buyer'] },
  { id: 'payments',           name: 'Платежи',           countKey: null,               hidden: false, allowedPartnerIds: [67, 497], roles: ['buyer'] },
  { id: 'received',           name: 'Получены',          countKey: 'received',         hidden: true,  allowedPartnerIds: null,      roles: ['buyer', 'seller'] },
  { id: 'finished',           name: 'Завершённые',       countKey: 'finished',         hidden: true,  allowedPartnerIds: null,      roles: ['buyer', 'seller'] },
  { id: 'deleted',            name: 'Отменённые',        countKey: 'deleted',          hidden: true,  allowedPartnerIds: null,      roles: ['buyer', 'seller'] },
] as const;

/**
 * Собирает вкладки под текущего партнёра.
 * Режет по роли и правам доступа, накладывает счётчики из getCounts.
 *
 * Функция модульного уровня, а не метод: нужна уже в data(), где методы
 * компонента ещё не гарантированы, и в this не нуждается.
 */
function buildFlowTabs(counts: Record<string, number> | null = null): TabElement[] {
  const partnerId = Core.currentPartner.id;

  let role: 'buyer' | 'seller';
  if (Core.currentPartner.isSeller) {
    role = 'seller';
  }
  else {
    role = 'buyer';
  }

  const result: TabElement[] = [];

  for (const declaration of FLOW_TABS) {

    // Роль
    if ((declaration.roles as readonly string[]).includes(role) === false) {
      continue;
    }

    // Права доступа: null = всем, иначе проверяем партнёра
    if (declaration.allowedPartnerIds === null) {
      // доступно всем — проверку пропускаем
    }
    else if ((declaration.allowedPartnerIds as readonly number[]).includes(partnerId) === false) {
      continue;
    }

    // Счётчик: только если у вкладки есть countKey
    let count: number | null;

    if (declaration.countKey === null) {
      count = null;
    }
    else {
      count = counts?.[declaration.countKey] ?? 0;
    }

    result.push(TabElement.from(declaration.id, declaration.name, count, declaration.hidden));
  }

  return result;
}

export default defineComponent({
  name: 'OrdersFlow',

  components: {
    OrdersRegistryExpanded,
    OrderPositionsRegistry,
    AssembliesRegistryExpanded,
    ShipmentsRegistryExpanded,
    OrdersPageFilterComponent,
    PaymentRequestsRegistry,
  },

  data() {
    return {
      filter:      OrdersPageFilter.fromLocalStorage(),
      filterClass: OrdersPageFilter,
      tabs:        buildFlowTabs(),

      /**
       * Активная вкладка — сообщает VTabsV2 (@update), страница роут
       * не парсит. Заполняется до mounted: VTabsV2 эмитит начальное
       * значение из своего created (immediate watch).
       */
      activeTab: null as TabElement | null,
    };
  },

  mounted() {
    this.loadCounts();
  },

  computed: {
    /**
     * state для OrderPositionsRegistry, если активная вкладка — реестр позиций,
     * иначе null (значит активна другая вкладка, реестр позиций не рендерится).
     */
    positionState(): string | null {
      const isPositionState = OrdersService.States.some(state => state.id === this.activeTab?.id);

      if (isPositionState) {
        return this.activeTab?.id ?? null;
      }
      else {
        return null;
      }
    },

    baseParams() {
      const params = this.filter.getRequestParams();

      // В реестре «в работе» у фильтра есть 2 доп. поля, которых не должно
      // быть в других реестрах — удаляем их, если активна не та вкладка.
      if (this.activeTab?.id !== 'in-work') {
        params.paymentStatus  = undefined;
        params.deliveryStatus = undefined;
      }

      return {
        ...params,
      }
    },

    registryParams() {
      return {
        ...this.baseParams,
        ...OrdersService.getStateFlag(this.positionState)
      }
    },

    /** «Готовы к отгрузке» — только сборки, отгруженные не полностью */
    assembliesParams() {
      return {
        ...this.baseParams,
        isShippedEntirely: false,
      }
    },
  },

  methods: {
    /** Активную вкладку сообщает VTabsV2 — из роута в router-mode */
    onTabUpdate(tab: TabElement | null) {
      this.activeTab = tab;
    },

    /** Применили фильтр — обновляем счётчики и активный реестр позиций */
    async onFilterUpdated(newFilter: OrdersPageFilter) {
      this.filter = newFilter;
      await this.loadCounts();
    },

    async loadCounts() {
      const payload = { ...this.registryParams };

      const counts = await OrdersService.getCounts(payload);

      if (!counts) {
        return;
      }
      else {
        this.tabs = buildFlowTabs(counts);
      }
    },
  },
});
</script>